import {
  Firestore,
  collection,
  doc,
  DocumentSnapshot,
  getDocs,
  getDoc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  where,
} from "firebase/firestore";
import app from "../config/firebase";
import { publicJsonRequest, PUBLIC_API_BASE } from "./publicApi";
import { CategorizedError } from "../types/errors";

import type {
  CPContestMonth,
  EventRecord,
  FaqEntry,
  LeaderboardDocument,
  TeamMember,
} from "../types/dataTypes";

type FetchOptions = {
  publishedOnly?: boolean;
  karmatekOnly?: boolean;
  upcomingOnly?: boolean;
  limit?: number;
  sortDesc?: boolean;
};

const cache = new Map<string, Promise<any>>();
let firestoreInstance: Firestore | null = null;

function getDb(): Firestore {
  if (firestoreInstance) return firestoreInstance;
  if (!app) throw new Error("Firebase app is not initialized.");
  firestoreInstance = getFirestore(app);
  return firestoreInstance;
}

function cacheResult<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (!cache.has(key)) {
    const promise = fetcher().finally(() => cache.delete(key));
    cache.set(key, promise);
  }
  return cache.get(key) as Promise<T>;
}

function mapDocTo<T>(doc: DocumentSnapshot): T & { id: string } {
  return { id: doc.id, ...(doc.data() as T) } as T & { id: string };
}

const EVENTS_COLLECTION = "events";
const TEAM_COLLECTION = "team";
const FAQ_COLLECTION = "faqs";
const CP_CONTEST_COLLECTION = "cp_cpcontest_winners";
const LEADERBOARD_COLLECTION = "leaderboards";
const ORDER_FIELD = "dateISO";

export async function fetchEvents(
  options?: FetchOptions,
): Promise<EventRecord[]> {
  try {
    const db = getDb();
    // Helpful debug: log project + collection + options in dev
    try {
      // eslint-disable-next-line no-console
      console.info(
        "fetchEvents: project=",
        (app as any)?.options?.projectId ?? "(unknown)",
        "collection=",
        EVENTS_COLLECTION,
        "options=",
        options,
      );
    } catch (e) {
      if ((import.meta as any).env?.DEV) {
        // Debug only: surface non-fatal errors during logging in development
        // eslint-disable-next-line no-console
        console.debug("fetchEvents: debug log failed:", e);
      }
    }
    const constraints: QueryConstraint[] = [];

    if (options?.publishedOnly) {
      constraints.push(where("published", "==", true));
    }
    if (options?.karmatekOnly) {
      constraints.push(where("karmatek", "==", true));
    }
    if (options?.upcomingOnly) {
      constraints.push(
        where("dateISO", ">=", new Date().toISOString().split("T")[0]),
      );
    }
    constraints.push(orderBy(ORDER_FIELD, options?.sortDesc ? "desc" : "asc"));
    if (options?.limit) {
      constraints.push(limit(options.limit));
    }

    const snap = await getDocs(
      query(collection(db, EVENTS_COLLECTION), ...constraints),
    );
    const docs = snap.docs.map((document) => mapDocTo<EventRecord>(document));
    // In development, log returned ids to make zero-results obvious
    if ((import.meta as any).env?.DEV) {
      try {
        // eslint-disable-next-line no-console
        console.debug(
          "fetchEvents: raw doc ids=",
          snap.docs.map((d) => d.id),
        );
      } catch (e) {
        if ((import.meta as any).env?.DEV) {
          // eslint-disable-next-line no-console
          console.debug("fetchEvents: dev debug failed:", e);
        }
      }
    }
    // eslint-disable-next-line no-console
    console.info(`fetchEvents: fetched ${docs.length} documents`);
    return docs;
  } catch (err) {
    // If Firestore access failed (app not initialized, network, etc.), return empty list
    // eslint-disable-next-line no-console
    console.warn(
      "fetchEvents failed:",
      err && (err as Error).message ? (err as Error).message : err,
    );
    try {
      // attempt a fallback: try fetching collection without ordering/constraints
      const db = getDb();
      const snap = await getDocs(query(collection(db, EVENTS_COLLECTION)));
      const docs = snap.docs.map((d) => mapDocTo<EventRecord>(d));
      // eslint-disable-next-line no-console
      console.debug(`fetchEvents fallback: fetched ${docs.length} documents`);
      if ((import.meta as any).env?.DEV) {
        try {
          console.debug(
            "fetchEvents fallback: raw doc ids=",
            snap.docs.map((d) => d.id),
          );
        } catch (e) {
          if ((import.meta as any).env?.DEV) {
            // eslint-disable-next-line no-console
            console.debug("fetchEvents fallback: debug failed:", e);
          }
        }
      }
      return docs;
    } catch (innerErr) {
      // eslint-disable-next-line no-console
      console.warn("fetchEvents fallback failed:", innerErr);
      // Throw categorized error for user-friendly display
      throw new CategorizedError(
        "Failed to load events from database",
        "database-error",
      );
    }
  }
}

export async function fetchAllEventsRaw(): Promise<EventRecord[]> {
  const db = getDb();
  const snap = await getDocs(collection(db, EVENTS_COLLECTION));
  return snap.docs.map((document) => mapDocTo<EventRecord>(document));
}

export async function fetchEventsByTag(tag: string): Promise<EventRecord[]> {
  const db = getDb();
  const q = query(
    collection(db, EVENTS_COLLECTION),
    where("tags", "array-contains", tag),
    orderBy(ORDER_FIELD, "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc) => mapDocTo<EventRecord>(doc));
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const db = getDb();
  return cacheResult("team", async () => {
    const snap = await getDocs(
      query(collection(db, TEAM_COLLECTION), orderBy("order", "asc")),
    );
    return snap.docs.map((doc) => mapDocTo<TeamMember>(doc));
  });
}

export async function fetchFaqEntries(): Promise<FaqEntry[]> {
  return cacheResult("faqs", async () => {
    try {
      const db = getDb();
      const snap = await getDocs(
        query(collection(db, FAQ_COLLECTION), orderBy("order", "asc")),
      );
      const docs = snap.docs.map((doc) => mapDocTo<FaqEntry>(doc));
      if (!docs.length && (import.meta as any).env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn("fetchFaqEntries: no docs found");
      }
      return docs;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("fetchFaqEntries failed", error);
      return [];
    }
  });
}

export async function fetchCpContestMonths(): Promise<CPContestMonth[]> {
  // If running in the browser, always use the public server API to fetch
  // cp contest months. Do NOT fall back to direct Firestore client access
  // from the browser — server will proxy the read using Admin credentials.
  if (typeof window !== "undefined") {
    const base = (PUBLIC_API_BASE || "").replace(/\/$/, "");
    if (!base) {
      throw new Error("Public API base is not configured");
    }
    const resp = await publicJsonRequest<{ contests: CPContestMonth[] }>(
      "/cp-contests",
    );
    return resp?.contests || [];
  }

  const db = getDb();
  return cacheResult("cp_cpcontest_winners", async () => {
    const snap = await getDocs(
      query(collection(db, CP_CONTEST_COLLECTION), orderBy("monthISO", "desc")),
    );
    return snap.docs.map((doc) => mapDocTo<CPContestMonth>(doc));
  });
}

export function listenLeaderboard(
  eventId: string,
  callback: (doc: LeaderboardDocument | null) => void,
): () => void {
  const db = getDb();
  const docRef = doc(db, LEADERBOARD_COLLECTION, eventId);
  return onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    callback(mapDocTo<LeaderboardDocument>(snapshot));
  });
}

export function listenAllLeaderboards(
  callback: (docs: LeaderboardDocument[]) => void,
): () => void {
  const db = getDb();
  return onSnapshot(collection(db, LEADERBOARD_COLLECTION), (snap) => {
    const docs = snap.docs.map((d) => {
      const mapped = mapDocTo<LeaderboardDocument>(d);
      return { ...mapped, eventId: mapped.eventId || mapped.id };
    });
    callback(docs);
  });
}

export async function fetchAllLeaderboards(): Promise<LeaderboardDocument[]> {
  const db = getDb();
  return cacheResult("leaderboards", async () => {
    const snap = await getDocs(query(collection(db, LEADERBOARD_COLLECTION)));
    return snap.docs.map((d) => {
      const doc = mapDocTo<LeaderboardDocument>(d);
      return {
        ...doc,
        eventId: doc.eventId || doc.id,
      };
    });
  });
}

export async function fetchKarmatekSchedule(): Promise<any | null> {
  const db = getDb();
  const ref = doc(db, "karmatek_schedule", "default");
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return mapDocTo<any>(snap);
}

export function clearFirestoreCache(): void {
  cache.clear();
}
